import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import userSourceScopeService from '../../services/userSourceScopeService';
import SourceScopeEditor from './SourceScopeEditor';
import SourceScopeBadge from './SourceScopeBadge';
import LoadingSpinner from '../common/LoadingSpinner';
import { getTimeZoneOptions, detectUserTimezone } from '../../utils/timezone';

const AVAILABLE_SOURCES = ['Chameleon', 'FavorDrop', 'DFC', 'None'];

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    companyName: '',
    role: 'SR',
    timezone: detectUserTimezone(),
    isActive: true,
  });

  const [scopeMode, setScopeMode] = useState('all');
  const [scopeSources, setScopeSources] = useState([]);

  /* R5-A — deactivation dialog state */
  const [deactivateDialog, setDeactivateDialog] = useState(null);
  const [deactivateSaving, setDeactivateSaving] = useState(false);
  const [deactivateError, setDeactivateError] = useState('');

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await api.getUsers();
      setUsers(data);
    } catch (err) {
      console.error('Failed to load users:', err);
      alert('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const payload = { ...formData };
        if (!payload.password) delete payload.password;
        await api.updateUser(editingUser.user_id, payload);
      } else {
        await api.createUser(formData);
      }

      if (editingUser) {
        const scopePayload =
          scopeMode === 'all'
            ? { mode: 'all' }
            : scopeMode === 'none'
              ? { mode: 'none' }
              : { mode: 'specific', sources: scopeSources };
        await userSourceScopeService.set(editingUser.user_id, scopePayload);
      }

      loadUsers();
      setShowAdd(false);
      setEditingUser(null);
      setFormData({
        email: '', password: '', fullName: '', companyName: '',
        role: 'SR', timezone: detectUserTimezone(), isActive: true,
      });
      setScopeMode('all');
      setScopeSources([]);
    } catch (err) {
      console.error('Failed to save user:', err);
      alert(err?.response?.data?.error || 'Failed to save user');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      fullName: user.full_name,
      companyName: user.company_name || '',
      role: user.role,
      timezone: user.timezone || detectUserTimezone(),
      isActive: user.is_active === 1 || user.is_active === true,
    });

    const scope = user.allowed_lead_sources;
    if (scope === null || scope === undefined) {
      setScopeMode('all');
      setScopeSources([]);
    } else if (Array.isArray(scope) && scope.length === 0) {
      setScopeMode('none');
      setScopeSources([]);
    } else {
      setScopeMode('specific');
      setScopeSources(Array.isArray(scope) ? scope : []);
    }

    setShowAdd(true);
  };

  /* R5-A — open deactivation dialog */
  const openDeactivate = async (user) => {
    setDeactivateError('');
    setDeactivateDialog({ user, preview: null, reassignTo: '' });
    try {
      const preview = await api.userDeactivationPreview(user.user_id);
      setDeactivateDialog((d) => d && { ...d, preview });
    } catch (err) {
      setDeactivateError(err?.response?.data?.error || 'Failed to load preview');
    }
  };

  /* R5-A — confirm deactivation */
  const confirmDeactivate = async () => {
    if (!deactivateDialog) return;
    const { user, preview, reassignTo } = deactivateDialog;
    setDeactivateError('');

    if (preview?.ownedLeads > 0 && !reassignTo) {
      setDeactivateError('Choose a reassignee for this user\'s leads');
      return;
    }

    setDeactivateSaving(true);
    try {
      await api.deactivateUser(user.user_id, reassignTo || undefined);
      setDeactivateDialog(null);
      await loadUsers();
    } catch (err) {
      setDeactivateError(err?.response?.data?.error || 'Failed to deactivate user');
    } finally {
      setDeactivateSaving(false);
    }
  };

  /* R5-A — reactivate */
  const handleReactivate = async (user) => {
    if (!window.confirm(`Reactivate ${user.full_name}?`)) return;
    try {
      await api.reactivateUser(user.user_id);
      await loadUsers();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to reactivate user');
    }
  };

  if (loading) return <LoadingSpinner message="Loading users..." />;

  return (
    <div className="user-management">
      <div className="management-header">
        <h2>User Management</h2>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
          <i className="fas fa-plus"></i> Add User
        </button>
      </div>

      {showAdd && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{editingUser ? 'Edit User' : 'Add New User'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  className="form-control"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={!!editingUser}
                />
              </div>

              <div className="form-group">
                <label>{editingUser ? 'New Password (leave blank to keep current)' : 'Password *'}</label>
                <input
                  type="password"
                  className="form-control"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                />
              </div>

              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Company Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Role</label>
                <select
                  className="form-control"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  <option value="SR">Sales Representative</option>
                  <option value="SM">Sales Manager</option>
                  <option value="Admin">Administrator</option>
                </select>
              </div>

              {editingUser && (
                <div className="form-group">
                  <label>Account Status</label>
                  <select
                    className="form-control"
                    value={formData.isActive ? '1' : '0'}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.value === '1' })
                    }
                  >
                    <option value="1">Active — user can log in</option>
                    <option value="0">Inactive — user cannot log in</option>
                  </select>
                </div>
              )}

              {editingUser && (
                <div className="form-group">
                  <label>Lead Source Scope</label>
                  <SourceScopeEditor
                    mode={scopeMode}
                    sources={scopeSources}
                    available={AVAILABLE_SOURCES}
                    onChange={({ mode, sources }) => {
                      setScopeMode(mode);
                      setScopeSources(sources);
                    }}
                  />
                </div>
              )}

              <div className="form-group">
                <label>Time Zone</label>
                <select
                  className="form-control"
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                >
                  {getTimeZoneOptions().map(tz => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAdd(false);
                    setEditingUser(null);
                    setFormData({
                      email: '', password: '', fullName: '', companyName: '',
                      role: 'SR', timezone: detectUserTimezone(), isActive: true,
                    });
                    setScopeMode('all');
                    setScopeSources([]);
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingUser ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Company</th>
              <th>Role</th>
              <th>Scope</th>
              <th>Time Zone</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const isSoftDeleted = !!user.deleted_at;
              return (
                <tr key={user.user_id}>
                  <td><strong>{user.full_name}</strong></td>
                  <td>{user.email}</td>
                  <td>{user.company_name || 'N/A'}</td>
                  <td><span className="badge badge-primary">{user.role}</span></td>
                  <td>
                    <SourceScopeBadge allowedLeadSources={user.allowed_lead_sources} />
                  </td>
                  <td>{user.timezone || 'Asia/Kolkata'}</td>
                  <td>
                    <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                      {isSoftDeleted ? 'Deactivated' : user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="actions">
                    {!isSoftDeleted && (
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleEdit(user)}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                    )}
                    {isSoftDeleted || !user.is_active ? (
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => handleReactivate(user)}
                      >
                        <i className="fas fa-undo"></i> Reactivate
                      </button>
                    ) : (
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => openDeactivate(user)}
                      >
                        <i className="fas fa-user-slash"></i> Deactivate
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* R5-A — Deactivation dialog */}
      {deactivateDialog && (
        <div
          className="modal-overlay"
          onMouseDown={() => !deactivateSaving && setDeactivateDialog(null)}
        >
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <h2>Deactivate {deactivateDialog.user.full_name}</h2>

            {deactivateError && (
              <div className="error-message">
                <i className="fas fa-exclamation-circle" /> {deactivateError}
              </div>
            )}

            {!deactivateDialog.preview ? (
              <LoadingSpinner message="Loading impact..." />
            ) : (
              <>
                <p className="text-muted">
                  This user will no longer be able to log in. Their leads and history
                  will be preserved.
                </p>

                {deactivateDialog.preview.ownedLeads > 0 ? (
                  <>
                    <div className="error-message" style={{ marginBottom: 12 }}>
                      <i className="fas fa-exclamation-triangle" /> This user owns{' '}
                      <strong>{deactivateDialog.preview.ownedLeads} lead(s)</strong>.
                      Choose a reassignee before deactivating.
                    </div>

                    <div className="form-group">
                      <label>Reassign leads to *</label>
                      <select
                        className="form-control"
                        value={deactivateDialog.reassignTo}
                        onChange={(e) =>
                          setDeactivateDialog((d) => ({ ...d, reassignTo: e.target.value }))
                        }
                      >
                        <option value="">Select a user...</option>
                        {deactivateDialog.preview.reassignCandidates.map((c) => (
                          <option key={c.user_id} value={c.user_id}>
                            {c.full_name} ({c.role})
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                ) : (
                  <p>
                    <i className="fas fa-check-circle" style={{ color: '#2e9c4a' }} />{' '}
                    This user owns no leads. Safe to deactivate.
                  </p>
                )}
              </>
            )}

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setDeactivateDialog(null)}
                disabled={deactivateSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmDeactivate}
                disabled={
                  deactivateSaving ||
                  !deactivateDialog.preview ||
                  (deactivateDialog.preview.ownedLeads > 0 && !deactivateDialog.reassignTo)
                }
              >
                {deactivateSaving ? 'Deactivating...' : 'Deactivate User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;